import { Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';

function SectionLabel({ text }: { text: string }) {
  return (
    <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
      <span
        className="bg-black/60 text-white font-bold text-center leading-tight rounded px-1 py-0.5 uppercase"
        style={{ fontSize: 4.5, maxWidth: '85%' }}
      >
        {text}
      </span>
    </div>
  );
}

const PHONE_BASE_W = 108;
const PHONE_BASE_H = PHONE_BASE_W * 16 / 9; // 192
const PHONE_SCALE = 1.5;

function PhoneFrame({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="relative bg-zinc-950 rounded-xl border-2 border-zinc-700 overflow-hidden shadow-lg flex-shrink-0"
      style={{ width: PHONE_BASE_W, aspectRatio: '9/16' }}
    >
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-7 h-1 bg-zinc-700 rounded-b-full z-20" />
      {children}
    </div>
  );
}

function PhoneWrapper({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="flex-shrink-0 relative"
      style={{
        width: PHONE_BASE_W * PHONE_SCALE,
        height: (PHONE_BASE_H + 28) * PHONE_SCALE,
      }}
    >
      <div style={{ transform: `scale(${PHONE_SCALE})`, transformOrigin: 'top left' }}>
        {children}
      </div>
    </div>
  );
}


function LayoutModelA() {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center gap-2">
      <PhoneFrame>
        {/* Top bar: profile */}
        <div className="absolute top-0 w-full bg-white flex items-center gap-1 px-1.5" style={{ height: 18, paddingTop: 3 }}>
          <div className="w-3 h-3 rounded-full bg-green-800 border border-green-950 flex-shrink-0" />
          <div>
            <div className="font-bold text-zinc-900 leading-none" style={{ fontSize: 4 }}>The Clippers</div>
            <div className="text-zinc-500 leading-none" style={{ fontSize: 3.5 }}>@autoclipper.live</div>
          </div>
        </div>
        {/* Title strip */}
        <div className="absolute w-full bg-white flex items-center px-1.5" style={{ top: 18, height: 11 }}>
          <span className="text-zinc-900 font-bold tracking-wider" style={{ fontSize: 4.5 }}>SUA HEADLINE AQUI</span>
        </div>
        {/* Split area */}
        <div className="absolute flex" style={{ top: 29, bottom: 0, left: 0, right: 0 }}>
          {/* Left: clip */}
          <div
            className="flex-1 relative overflow-hidden"
            style={{ background: 'linear-gradient(150deg, #1a4010 0%, #0c2208 100%)' }}
          >
            <SectionLabel text="Seu clipe editado" />
          </div>
          {/* Vertical red divider */}
          <div className="w-px bg-red-500 flex-shrink-0" />
          {/* Right column: two quadrants */}
          <div className="flex flex-col" style={{ width: '52%' }}>
            {/* Top right: mandatory video */}
            <div
              className="flex-1 relative bg-zinc-900 flex items-center justify-center"
              style={{ border: '1px solid #ef4444', borderTop: 'none', borderRight: 'none' }}
            >
              <span
                className="text-white font-bold text-center leading-tight uppercase"
                style={{ fontSize: 4.5, maxWidth: '80%' }}
              >
                {'VÍDEO\nOBRIGATÓRIO'}
              </span>
            </div>
            {/* Horizontal red divider */}
            <div className="h-px bg-red-500 flex-shrink-0" />
            {/* Bottom right: free content */}
            <div
              className="flex-1 relative bg-zinc-900 flex items-center justify-center px-1"
              style={{ border: '1px solid #ef4444', borderBottom: 'none', borderRight: 'none' }}
            >
              <span
                className="text-white font-bold text-center leading-tight uppercase"
                style={{ fontSize: 3.8, maxWidth: '90%' }}
              >
                {'IMAGEM OU\nCONTEÚDO\nLIVRE'}
              </span>
            </div>
          </div>
        </div>
      </PhoneFrame>
      <span className="text-muted-foreground font-medium text-center" style={{ fontSize: 10, maxWidth: 108 }}>
        {t('campaign.rules.layoutModels.model1Caption')}
      </span>
    </div>
  );
}

function LayoutModelC() {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center gap-2">
      <PhoneFrame>
        {/* Top bar: profile */}
        <div className="absolute top-0 w-full bg-white flex items-center gap-1 px-1.5" style={{ height: 18, paddingTop: 3 }}>
          <div className="w-3 h-3 rounded-full bg-green-800 border border-green-950 flex-shrink-0" />
          <div>
            <div className="font-bold text-zinc-900 leading-none" style={{ fontSize: 4 }}>The Clippers</div>
            <div className="text-zinc-500 leading-none" style={{ fontSize: 3.5 }}>@autoclipper.live</div>
          </div>
        </div>
        {/* Title strip */}
        <div className="absolute w-full bg-white flex items-center px-1.5" style={{ top: 18, height: 11 }}>
          <span className="text-zinc-900 font-bold tracking-wider" style={{ fontSize: 4.5 }}>SUA HEADLINE AQUI</span>
        </div>
        {/* Split area: 50/50 */}
        <div className="absolute flex" style={{ top: 29, bottom: 0, left: 0, right: 0 }}>
          {/* Left 50%: clip */}
          <div
            className="relative overflow-hidden"
            style={{ width: '50%', background: 'linear-gradient(150deg, #1a4010 0%, #0c2208 100%)' }}
          >
            <SectionLabel text="Seu clipe editado" />
          </div>
          {/* Vertical red divider */}
          <div className="w-px bg-red-500 flex-shrink-0" />
          {/* Right 50%: mandatory video */}
          <div className="flex-1 relative bg-zinc-900">
            <SectionLabel text="Vídeo obrigatório" />
          </div>
        </div>
      </PhoneFrame>
      <span className="text-muted-foreground font-medium text-center" style={{ fontSize: 10, maxWidth: 108 }}>
        {t('campaign.rules.layoutModels.model3Caption')}
      </span>
    </div>
  );
}

function LayoutModelB() {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center gap-2">
      <PhoneFrame>
        {/* Top 53%: clip footage */}
        <div
          className="absolute top-0 left-0 right-0"
          style={{ height: '53%', background: 'linear-gradient(180deg, #1a3a0a 0%, #0d2206 100%)' }}
        >
          <SectionLabel text="Seu clipe editado" />
        </div>
        {/* Middle 13%: headline band — straight edges, red */}
        <div
          className="absolute left-0 right-0 bg-red-600 flex items-center justify-center px-2"
          style={{ top: '53%', height: '13%' }}
        >
          <span className="text-white font-black text-center leading-tight uppercase" style={{ fontSize: 5, whiteSpace: 'pre-line' }}>
            SUA HEADLINE AQUI
          </span>
        </div>
        {/* Bottom 34%: mandatory video */}
        <div
          className="absolute bottom-0 left-0 right-0 bg-zinc-950 flex items-center justify-center"
          style={{ top: '66%' }}
        >
          <SectionLabel text="Vídeo obrigatório" />
        </div>
        <div className="absolute bottom-1 right-1 text-zinc-700 opacity-50" style={{ fontSize: 3.5 }}>Clip&amp;Pix</div>
      </PhoneFrame>
      <span className="text-muted-foreground font-medium text-center" style={{ fontSize: 10, maxWidth: 108 }}>
        {t('campaign.rules.layoutModels.model2Caption')}
      </span>
    </div>
  );
}

export function CampaignLayoutModelsGuide() {
  const { t } = useTranslation();

  return (
    <div className="space-y-5">
      <div className="flex items-start gap-2.5 p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
        <Info className="w-4 h-4 text-blue-400 mt-0.5 shrink-0" />
        <p className="text-sm text-blue-200/80 leading-relaxed">
          {t('campaign.rules.layoutModels.intro')}
        </p>
      </div>

      <div className="flex flex-wrap gap-8 justify-center sm:justify-start">
        <PhoneWrapper><LayoutModelA /></PhoneWrapper>
        <PhoneWrapper><LayoutModelB /></PhoneWrapper>
        <PhoneWrapper><LayoutModelC /></PhoneWrapper>
      </div>

      <p className="text-xs text-muted-foreground italic mt-8">
        {t('campaign.rules.layoutModels.hint')}
      </p>
    </div>
  );
}
